import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { HttpRetryableService } from './http-retryable.service';
import { NO_RETRY_NETWORK_CONFIG } from '../utils/network-retry-strategy';

export interface RemoveAccountResponse {
  success: boolean;
  data: {
    businessKey: string;
  };
}

export type UserRemovalStatus = 'Failed' | 'Completed' | 'InProgress';

export interface UserRemovalStatusResponse {
  success: boolean;
  data: {
    status: UserRemovalStatus;
  };
}

export interface LoginResponse {
  success: boolean;
  data: {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    refresh_expires_in: number;
  };
}

/**
 * Ответ обмена одноразового кода имперсонации на токены целевого пользователя. Совпадает по составу
 * с {@link LoginResponse}: и обычный вход, и имперсонация возвращают только токены. Достаточно для
 * просмотровой сессии администратора.
 */
export interface ImpersonationExchangeResponse {
  success: boolean;
  data: {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    refresh_expires_in: number;
  };
}

export interface UserDataResponse {
  success: boolean;
  data: UserData;
}

export interface UserData {
  contact: string;
  email: string;
  firstName: string;
  id: number;
  inHartia: string | null;
  inn: string;
  isRegistered: boolean;
  lastName: string;
  name: string;
  ogrn: string;
  patronymicName: string;
  phone: string;
  roles: string[];
  type: string;
}

export enum Platform {
  WEB = 'Web',
  IOS = 'iOS',
  ANDROID = 'Android',
  HUAWEI = 'Huawei'
}

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private baseRestURL = '';

  constructor(
    private http: HttpClient,
    private httpRetryable: HttpRetryableService,
    configService: ConfigService
  ) {
    this.baseRestURL = configService.baseRestURL;
  }

  public login(
    user: string,
    password: string,
    deviceToken: string,
    platformName: string,
    group: string
  ) {
    let platform;
    switch (platformName) {
      case 'ios':
        platform = Platform.IOS;
        break;
      case 'android':
        platform = Platform.ANDROID;
        break;
      case 'huawei':
        platform = Platform.HUAWEI;
        break;
      default:
        platform = Platform.WEB;
        break;
    }

    const body = {
      user,
      password,
      deviceToken,
      platform,
      group
    };

    return this.httpRetryable.post<LoginResponse>(
      `${this.baseRestURL}usermanagement/registration/platform-login`,
      body,
      { observe: 'response' }
    );
  }

  /**
   * Обмен одноразового кода имперсонации (из redirect Identix на /impersonation/callback?code=…) на
   * токены целевого пользователя. Без повторов: код одноразовый — повтор с уже использованным кодом
   * бессмыслен и вернул бы ошибку.
   */
  public exchangeImpersonationCode(
    code: string
  ): Observable<ImpersonationExchangeResponse> {
    return this.httpRetryable.post<ImpersonationExchangeResponse>(
      `${this.baseRestURL}usermanagement/impersonation/exchange`,
      { code },
      { retryConfig: NO_RETRY_NETWORK_CONFIG }
    );
  }

  public resetPassword(email: string) {
    const params = new HttpParams().set('email', email);

    return this.http.post<LoginResponse>(
      `${this.baseRestURL}usermanagement/registration/reset-password`,
      {},
      { params, observe: 'response' }
    );
  }

  public refreshToken(refreshToken: string): Observable<LoginResponse> {
    const curUrl = `${this.baseRestURL}usermanagement/registration/refresh-token`;
    const params = new HttpParams().set('refreshToken', refreshToken);
    // Без повторов: refresh-токен ротационный — повтор со старым токеном при потерянном ответе
    // (status 0) получил бы 401 и разлогинил бы пользователя из-за микрообрыва сети.
    return this.httpRetryable.post<LoginResponse>(curUrl, params, {
      retryConfig: NO_RETRY_NETWORK_CONFIG
    });
  }

  public killAuthSession(refreshToken: string): Observable<LoginResponse> {
    const curUrl = `${this.baseRestURL}usermanagement/registration/v2/logout`;
    const params = new HttpParams().set('refreshToken', refreshToken);
    return this.http.post<LoginResponse>(curUrl, params);
  }

  public removeAccount(): Observable<RemoveAccountResponse> {
    return this.http.delete<RemoveAccountResponse>(
      `${this.baseRestURL}usermanagement/self-management/self-remove`
    );
  }

  public fetchRemovalStatus(
    businessKey: string
  ): Observable<UserRemovalStatusResponse> {
    return this.http.get<UserRemovalStatusResponse>(
      `${this.baseRestURL}usermanagement/self-management/removal-status/${encodeURIComponent(businessKey)}`
    );
  }
}
